import { useState, useEffect } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { X, Plus, Trash2 } from 'lucide-react'
import { adminApi } from '@/api/admin'
import type { CourseSessionDto, SessionRequest, TrainerRole, CoursePricingModel, DeliveryMethod } from '@/api/admin'
import { Button } from '@/components/ui/button'
import { TrainerAssignmentFields } from '@/components/shared/TrainerAssignmentFields'
import { DeliveryMethodFields } from '@/components/shared/DeliveryMethodFields'
import { useAdminMoney } from '@/hooks/useMoney'
import { planDays, splitMinutes, addMinutesToTime, nextWorkingDay } from '@/lib/duration'
import type { CourseDto } from '@/api/admin'

interface Props {
  session: CourseSessionDto | null
  onClose: () => void
  onSaved: () => void
}

// `manual` is set once someone edits a day's own fields, so the course's pre-filled days stop overwriting it.
type DayEntry = { date: string; startTime: string; endTime: string; manual?: boolean }
type TrainerEntry = { trainerId: string; role: TrainerRole }


function localToUtcIso(date: string, time: string): string {
  return new Date(`${date}T${time}`).toISOString()
}

/**
 * Re-applies a course's pre-filled days to a new session: later days follow the first day's start time and fall on the
 * next working days, and each day gets an equal share of the course's total time. Days someone has edited by hand are left alone.
 */
function reflowDays(days: DayEntry[], course: CourseDto | undefined): DayEntry[] {
  if (!course) return days
  const parts = course.durationMinutes != null ? splitMinutes(course.durationMinutes, days.length) : null
  const out: DayEntry[] = []
  days.forEach((d, i) => {
    const next = { ...d }
    if (i > 0 && !d.manual) {
      next.startTime = out[0].startTime
      next.date = out[i - 1].date ? nextWorkingDay(out[i - 1].date) : ''
    }
    if (parts && !d.manual) next.endTime = addMinutesToTime(next.startTime, parts[i])
    out.push(next)
  })
  return out
}

function fromIso(iso: string): { date: string; startTime: string } {
  const d = new Date(iso)
  const date = d.toLocaleDateString('sv') // YYYY-MM-DD in local time
  const startTime = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false })
  return { date, startTime }
}

export default function SessionFormModal({ session, onClose, onSaved }: Props) {
  const { symbol } = useAdminMoney()
  const isEdit = !!session

  const { data: courses = [] } = useQuery({
    queryKey: ['courses'],
    queryFn: () => adminApi.getCourses().then(r => r.data),
  })

  const { data: venues = [] } = useQuery({
    queryKey: ['venues'],
    queryFn: () => adminApi.getVenues().then(r => r.data),
  })

  const { data: trainers = [] } = useQuery({
    queryKey: ['trainers'],
    queryFn: () => adminApi.getTrainers().then(r => r.data),
  })

  function initDays(): DayEntry[] {
    if (!session) return [{ date: '', startTime: '09:00', endTime: '' }]

    const { date: d0, startTime: s0 } = fromIso(session.startsAt)
    const e0 = session.endsAt ? fromIso(session.endsAt).startTime : ''

    const primary: DayEntry = { date: d0, startTime: s0, endTime: e0 }

    const extra: DayEntry[] = (session.additionalDays ?? []).map(d => {
      const { date, startTime } = fromIso(d.startsAt)
      const endTime = d.endsAt ? fromIso(d.endsAt).startTime : ''
      return { date, startTime, endTime }
    })

    return [primary, ...extra]
  }

  const [courseId, setCourseId] = useState(session?.courseId ?? '')
  const [venueId, setVenueId] = useState(session?.venue?.id ?? '')
  const [deliveryMethod, setDeliveryMethod] = useState<DeliveryMethod>(session?.deliveryMethod ?? 'InPerson')
  const [meetingUrl, setMeetingUrl] = useState(session?.meetingUrl ?? '')
  const [meetingInstructions, setMeetingInstructions] = useState(session?.meetingInstructions ?? '')
  const [days, setDays] = useState<DayEntry[]>(initDays)
  const [totalPlaces, setTotalPlaces] = useState(String(session?.totalPlaces ?? 10))
  const [price, setPrice] = useState(session?.price !== null && session?.price !== undefined ? String(session.price) : '')
  const [pricingModel] = useState<CoursePricingModel>(session?.pricingModel ?? 'PerPerson')
  const [flatFeeAmount] = useState(session?.flatFeeAmount !== null && session?.flatFeeAmount !== undefined ? String(session.flatFeeAmount) : '')
  const [flatFeeCapacity] = useState(session?.flatFeeCapacity !== null && session?.flatFeeCapacity !== undefined ? String(session.flatFeeCapacity) : '')
  const [status, setStatus] = useState<string>(session?.status ?? 'Draft')
  const [notes, setNotes] = useState(session?.notes ?? '')
  const [trainerAssignments, setTrainerAssignments] = useState<TrainerEntry[]>(
    session?.trainerAssignments?.map(t => ({ trainerId: t.trainerId, role: t.role })) ?? []
  )

  const saveMutation = useMutation({
    mutationFn: (data: SessionRequest) =>
      isEdit
        ? adminApi.updateSession(session!.id, data)
        : adminApi.createSession(data),
    onSuccess: onSaved,
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const primary = days[0]
    if (!courseId || !primary.date || !primary.startTime || !totalPlaces) return

    const additionalDays = days.slice(1)
      .filter(d => d.date && d.startTime)
      .map(d => ({
        startsAt: localToUtcIso(d.date, d.startTime),
        endsAt: d.endTime ? localToUtcIso(d.date, d.endTime) : null,
      }))

    saveMutation.mutate({
      courseId,
      venueId: deliveryMethod === 'Online' ? null : (venueId || null),
      startsAt: localToUtcIso(primary.date, primary.startTime),
      endsAt: primary.endTime ? localToUtcIso(primary.date, primary.endTime) : null,
      totalPlaces: parseInt(totalPlaces),
      price: price !== '' ? parseFloat(price) : null,
      pricingModel,
      flatFeeAmount: flatFeeAmount !== '' ? parseFloat(flatFeeAmount) : null,
      flatFeeCapacity: flatFeeCapacity !== '' ? parseInt(flatFeeCapacity) : null,
      status,
      notes: notes || null,
      additionalDays,
      trainerAssignments,
      deliveryMethod,
      meetingUrl: deliveryMethod === 'Online' ? (meetingUrl.trim() || null) : null,
      meetingInstructions: deliveryMethod === 'Online' ? (meetingInstructions.trim() || null) : null,
    })
  }

  const selectedCourse = courses.find(c => c.id === courseId)

  // New sessions only: editing an existing session never changes its days by itself.
  const updateDay = (idx: number, field: 'date' | 'startTime' | 'endTime', value: string) =>
    setDays(prev => {
      // The first day's date and start time drive the others; anything else typed in is the person's own choice.
      const drives = idx === 0 && field !== 'endTime'
      const changed = prev.map((d, i) => i === idx ? { ...d, [field]: value, manual: drives ? d.manual : true } : d)
      return isEdit ? changed : reflowDays(changed, selectedCourse)
    })

  const addDay = () => setDays(prev => {
    const next = [...prev, { date: '', startTime: prev[0]?.startTime || '09:00', endTime: '' }]
    return isEdit ? next : reflowDays(next, selectedCourse)
  })

  const removeDay = (idx: number) => setDays(prev => {
    const next = prev.filter((_, i) => i !== idx)
    return isEdit ? next : reflowDays(next, selectedCourse)
  })

  const changeCourse = (id: string) => {
    setCourseId(id)
    if (isEdit) return
    const course = courses.find(c => c.id === id)
    if (!course) return
    setDays(prev => {
      if (prev.slice(1).some(d => d.manual)) return prev // days were already set by hand
      const first = prev[0]
      const planned = planDays(course, first.date, first.startTime || '09:00')
      return planned.map((p, i) => i === 0 ? { ...first, endTime: first.manual ? first.endTime : p.endTime } : p)
    })
  }

  // Pre-fill the per-person price from the selected course if creating and nothing set yet
  useEffect(() => {
    if (!isEdit && courseId && price === '') {
      const course = courses.find(c => c.id === courseId)
      if (!course || course.pricingModel === 'FlatFee') return
      if (course.price != null) setPrice(String(course.price))
    }
  }, [courseId, courses, isEdit, price])

  // Sessions are always priced per person. A session created with a flat fee before that rule keeps its pricing.
  const legacyFlatFee = isEdit && session?.pricingModel === 'FlatFee'

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
        <button type="button" onClick={onClose} className="absolute top-4 right-4 text-slate-400 hover:text-slate-600">
          <X size={18} />
        </button>
        <h2 className="text-base font-semibold text-slate-900 mb-5">
          {isEdit ? 'Edit public session' : 'New public session'}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Course <span className="text-red-500">*</span></label>
            <select
              required
              value={courseId}
              onChange={e => changeCourse(e.target.value)}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
            >
              <option value="">Select a course…</option>
              {courses.map(c => (
                <option key={c.id} value={c.id} disabled={c.pricingModel === 'FlatFee' && c.id !== session?.courseId}>
                  {c.name}{c.pricingModel === 'FlatFee' ? ' (flat fee - not available for sessions)' : ''}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-slate-400">Sessions are always priced per person, so flat-fee courses can't be used.</p>
          </div>

          <DeliveryMethodFields
            value={{ deliveryMethod, meetingUrl, meetingInstructions }}
            onChange={next => { setDeliveryMethod(next.deliveryMethod); setMeetingUrl(next.meetingUrl); setMeetingInstructions(next.meetingInstructions) }}
            venueField={
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Venue</label>
                <select
                  value={venueId}
                  onChange={e => setVenueId(e.target.value)}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                >
                  <option value="">Venue to be confirmed</option>
                  {venues.map(v => (
                    <option key={v.id} value={v.id}>{v.name}{v.city ? ` — ${v.city}` : ''}</option>
                  ))}
                </select>
              </div>
            }
          />

          {/* Days */}
          <div className="space-y-3">
            {days.map((day, idx) => (
              <div key={idx} className="rounded-lg border border-slate-200 p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                    {idx === 0 ? 'Date of training' : `Day ${idx + 1}`}
                  </span>
                  {idx > 0 && (
                    <button
                      type="button"
                      onClick={() => removeDay(idx)}
                      className="text-slate-400 hover:text-red-500 transition-colors"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
                <input
                  type="date"
                  required={idx === 0}
                  value={day.date}
                  onChange={e => updateDay(idx, 'date', e.target.value)}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                />
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Start time <span className="text-red-500">*</span></label>
                    <input
                      type="time"
                      step="300"
                      required={idx === 0}
                      value={day.startTime}
                      onChange={e => updateDay(idx, 'startTime', e.target.value)}
                      className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">End time</label>
                    <input
                      type="time"
                      step="300"
                      value={day.endTime}
                      onChange={e => updateDay(idx, 'endTime', e.target.value)}
                      className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                    />
                  </div>
                </div>
              </div>
            ))}

            <button
              type="button"
              onClick={addDay}
              className="flex items-center gap-1.5 text-sm text-indigo-600 hover:text-indigo-800 transition-colors"
            >
              <Plus size={15} /> Add another day
            </button>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Places <span className="text-red-500">*</span></label>
            <input
              type="number"
              min={1}
              required
              value={totalPlaces}
              onChange={e => setTotalPlaces(e.target.value)}
              className="w-full max-w-[8rem] rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
            />
          </div>

          {/* Pricing */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Price per person</label>
            {legacyFlatFee ? (
              <p className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
                This session was set up with a flat fee of {symbol}{flatFeeAmount || '0'} before sessions became per-person only, and keeps that pricing.
              </p>
            ) : (
              <input
                type="number"
                min={0}
                step="0.01"
                placeholder="Free"
                value={price}
                onChange={e => setPrice(e.target.value)}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                aria-label={`Price per person (${symbol})`}
              />
            )}
          </div>

          {/* Trainer assignments */}
          {trainers.length > 0 && (
            <TrainerAssignmentFields
              trainers={trainers.filter(t => t.isActive)}
              assignments={trainerAssignments}
              onChange={setTrainerAssignments}
            />
          )}

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Status</label>
            <select
              value={status}
              onChange={e => setStatus(e.target.value)}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
            >
              <option value="Draft">Draft</option>
              <option value="Published">Published</option>
              <option value="Completed">Completed</option>
              <option value="Cancelled">Cancelled</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Notes (internal)</label>
            <textarea
              rows={2}
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Any internal notes about this session…"
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 resize-none"
            />
          </div>

          {saveMutation.isError && (
            <p className="text-sm text-red-600">Failed to save. Please try again.</p>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saveMutation.isPending}>
              {saveMutation.isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Create session'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
